import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { useHasPermission } from '@/lib/permissions';
import { Button } from '@/components/ui/button';
import { MissionForm, toMissionPayload, type MissionFormData } from '@/components/missions/MissionForm';

import type { Mission } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/missions/new')({ component: MissionsNewPage });

function MissionsNewPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const canWrite = useHasPermission('missions.write');

  const mutation = useMutation({
    mutationFn: async (data: MissionFormData) => (await api.post<Mission>('/missions', toMissionPayload(data, 'create'))).data,
    onSuccess: (mission) => {
      void qc.invalidateQueries({ queryKey: ['missions'] });
      toast.success(`Mission ${mission.reference} créée`);
      void nav({ to: '/missions/$id', params: { id: mission.id } });
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Erreur lors de la création')),
  });

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/missions"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Link>
        </Button>
        <h1 className="text-2xl font-bold">Nouvelle mission</h1>
      </div>

      {canWrite ? (
        <MissionForm
          submitLabel="Créer la mission"
          pending={mutation.isPending}
          onSubmit={(d) => mutation.mutate(d)}
          onCancel={() => void nav({ to: '/missions' })}
        />
      ) : (
        <p className="text-muted-foreground">Vous n’avez pas accès à la création de missions.</p>
      )}
    </div>
  );
}
